import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Can from '../../components/Can';
import { useTranslation } from 'react-i18next';
import './StockLedger.css';

const fmt = (n) => fmtNum(n);
const inrL = (n) => fmtNum(n) + ' L';

export default function StockLedger() {
  const { user } = useAuth();
  const { t: tr } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today());
  const [tankId, setTank] = useState('');
  const [regen, setRegen] = useState(false);
  const [editDip, setEditDip] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  React.useEffect(() => {
    const d = new Date();
    const wk = new Date(d.getTime() - 7 * 86400 * 1000);
    setFrom(wk.toISOString().slice(0, 10));
  }, []);

  const { data: tanks } = useApi(() => api.stockLedger.tanks(), [], []);
  const { data, loading, error, reload } = useApi(
    () => from && to ? api.stockLedger.list(from, to, tankId || null) : Promise.resolve(null),
    [from, to, tankId],
    null
  );

  const handleRegenerate = async () => {
    if (!from || !to) { notify('❌ ' + tr('stockLedger.toasts.pickDateRange')); return; }
    setRegen(true);
    try {
      const res = await api.stockLedger.regenerate({ from, to, tank_id: tankId || null });
      notify('✅ ' + tr('stockLedger.toasts.regenerated', { n: res.regenerated }));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setRegen(false);
  };

  const handleDipSave = async (id, payload) => {
    try {
      await api.stockLedger.update(id, payload);
      notify('✅ ' + tr('stockLedger.toasts.saved'));
      setEditDip(null);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
  };

  return (
    <div className="sl-page">
      <div className="page-header">
        <h2 className="page-title">{tr('stockLedger.title')}</h2>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {tr('stockLedger.subtitle')}
        </span>
      </div>

      {msg && <div className="sl-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      <div className="sl-filters section-card">
        <div className="form-group">
          <label>{tr('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{tr('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{tr('common.tank')}</label>
          <select value={tankId} onChange={e => setTank(e.target.value)}>
            <option value="">{tr('stockLedger.allTanks')}</option>
            {(tanks || []).map(t => (
              <option key={t.id} value={t.id}>
                {tr('common.tank')} {t.tank_no} ({t.product_code})
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button className="btn btn-sm" onClick={reload}>🔄 {tr('common.refresh')}</button>
          <Can module="stock_ledger" action="update">
            <button className="btn btn-primary btn-sm" disabled={regen} onClick={handleRegenerate}>
              {regen ? '⏳ ' + tr('stockLedger.regenerating') : '🔁 ' + tr('stockLedger.regenerate')}
            </button>
          </Can>
        </div>
      </div>

      {data && data.totals && (
        <div className="sl-kpis">
          <Kpi label={tr('common.opening')} value={inrL(data.totals.opening)} />
          <Kpi label={tr('stockLedger.purchases')} value={inrL(data.totals.purchases)} tone="green" />
          <Kpi label={tr('stockLedger.sales')} value={inrL(data.totals.total_sales)} tone="blue" />
          <Kpi label={tr('common.closing')} value={inrL(data.totals.closing)} tone="orange" />
          <Kpi label={tr('stockLedger.daysTanks')} value={data.totals.count} />
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{tr('stockLedger.dailyStockRows')}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{tr('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.tank')}</th>
                <th style={{ textAlign: 'left' }}>{tr('common.product')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.opening')}</th>
                <th style={{ textAlign: 'right' }}>{tr('stockLedger.purchases')}</th>
                <th style={{ textAlign: 'right' }}>{tr('stockLedger.sales')}</th>
                <th style={{ textAlign: 'right' }}>{tr('stockLedger.testing')}</th>
                <th style={{ textAlign: 'right' }}>{tr('stockLedger.evap')}</th>
                <th style={{ textAlign: 'right' }}>{tr('common.closing')}</th>
                <th style={{ textAlign: 'right' }}>{tr('stockLedger.physicalDip')}</th>
                <th style={{ textAlign: 'right' }}>{tr('stockLedger.variance')}</th>
                <th style={{ textAlign: 'center' }}>{tr('common.action')}</th>
              </tr>
            </thead>
            <tbody>
              {(!data || !data.rows || data.rows.length === 0) ? (
                <tr><td colSpan="12" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  {tr('stockLedger.noRows')}
                </td></tr>
              ) : data.rows.map(r => (
                <tr key={r.id}>
                  <td style={{ textAlign: 'left', fontSize: 13 }}>{fmtDate(r.stock_date)}</td>
                  <td style={{ textAlign: 'left', fontWeight: 600 }}>{tr('common.tank')} {r.tank_no}</td>
                  <td style={{ textAlign: 'left' }}>
                    <span className="sl-product-pill">{r.product_code}</span>
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.opening_stock)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', color: Number(r.purchases) > 0 ? 'var(--secondary)' : 'inherit', fontWeight: Number(r.purchases) > 0 ? 700 : 400 }}>
                    {fmt(r.purchases)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', color: Number(r.total_sales) > 0 ? '#2b6cb0' : 'inherit' }}>
                    {fmt(r.total_sales)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{fmt(r.testing_loss)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>{fmt(r.evap_loss)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)' }}>{fmt(r.closing_stock)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>
                    {r.physical_dip !== null ? fmt(r.physical_dip) : '—'}
                  </td>
                  <td style={{
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: r.variance === null ? 'var(--text-muted)'
                      : Math.abs(Number(r.variance)) < 0.1 ? 'var(--secondary)'
                        : 'var(--danger)'
                  }}>
                    {r.variance !== null ? (Number(r.variance) > 0 ? '+' : '') + fmt(r.variance) : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Can module="stock_ledger" action="update">
                      <button className="btn btn-sm" onClick={() => setEditDip(r)}>📏 {tr('stockLedger.dip')}</button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editDip && (
        <DipModal row={editDip} onSave={handleDipSave} onClose={() => setEditDip(null)} />
      )}
    </div>
  );
}


/* ── Dip measurement modal ── */
function DipModal({ row, onSave, onClose }) {
  const { t: tr } = useTranslation();
  const [dip, setDip] = useState(row.physical_dip ?? '');
  const [notes, setNotes] = useState(row.notes || '');

  return (
    <div className="sl-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sl-modal">
        <h3>{tr('stockLedger.dipModal.title', { tank: row.tank_no, date: fmtDate(row.stock_date) })}</h3>
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '8px 0' }}>
          {tr('stockLedger.dipModal.computedClosing')} <strong>{fmt(row.closing_stock)} L</strong>
        </p>
        <div className="form-group">
          <label>{tr('stockLedger.dipModal.dipReading')}</label>
          <input type="number" step="0.001" value={dip}
            onChange={e => setDip(e.target.value)}
            placeholder={tr('stockLedger.dipModal.leaveEmpty')} />
        </div>
        {dip !== '' && !isNaN(parseFloat(dip)) && (
          <p style={{ fontSize: 13 }}>
            {tr('stockLedger.variance')}: <strong style={{
              color: Math.abs(parseFloat(dip) - Number(row.closing_stock)) < 1
                ? 'var(--secondary)' : 'var(--danger)'
            }}>
              {((parseFloat(dip) - Number(row.closing_stock)) >= 0 ? '+' : '')}
              {(parseFloat(dip) - Number(row.closing_stock)).toFixed(2)} L
            </strong>
          </p>
        )}
        <div className="form-group">
          <label>{tr('common.notes')}</label>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            style={{ width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 4 }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="btn btn-sm" onClick={onClose}>{tr('common.cancel')}</button>
          <button className="btn btn-primary btn-sm"
            onClick={() => onSave(row.id, { physical_dip: dip === '' ? null : parseFloat(dip), notes })}>
            {tr('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}


function Kpi({ label, value, tone }) {
  return (
    <div className={`sl-kpi ${tone ? 'kpi-' + tone : ''}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
}

